import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Form, Button, Space, Empty, Spin, Row, Col, Typography, Flex, Input, Select, Tag, message, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { PlusOutlined, DatabaseOutlined, ArrowLeftOutlined, HighlightOutlined, QuestionCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import BeautifyControl from '../../../Shared/BeautifyControl/BeautifyControl';
import { ICON_SIZE } from '../../../Shared/Styles/iconButton';
import debounce from 'lodash/debounce';
import { getknowledgeDatabases, addKnowledgeDatabase, createOrUpdateQAEntry, createOrUpdateBusinessInfoEntry, deleteEntry, beautifyEntry } from '../../../KnowledgeDatabase/KnowledgeDatabaseServices';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import QAEntryItem from './QAEntryItem/QAEntryItem';
import BusinessInfoEntryItem from './BusinessInfoEntryItem/BusinessInfoEntryItem';
import { useErrorHandler } from '../../../../Hooks/useErrorHandler';
import { resolveBackendMessage } from '../../../../Utils/apiMessages';

const { Text } = Typography;


const KnowledgeBaseSection = ({ form, navigate, initialData, onUpdate, onStartCreate, onCreated, onAssigned }) => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const { handleError } = useErrorHandler();
    const [createForm] = Form.useForm();
    const [qaForm] = Form.useForm();
    const [businessForm] = Form.useForm();
    const [creating, setCreating] = useState(false);
    const [addingEntry, setAddingEntry] = useState(false);
    const [currentKB, setCurrentKB] = useState(null);
    const [entryTypeTab, setEntryTypeTab] = useState('qa');
    const [isCreatingKB, setIsCreatingKB] = useState(false);
    const [entries, setEntries] = useState({ qa: [], business: [] });

    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNoKnowledgeBases, setHasNoKnowledgeBases] = useState(false);
    const [selectedKnowledgeBases, setSelectedKnowledgeBases] = useState([]);
    const pageSize = 10;

    const [currentStep, setCurrentStep] = useState(0);
    const [createdKB, setCreatedKB] = useState(null);


    // Modify the styles definition at the top to be more specific
    const nonInteractiveStyles = {
        cursor: 'default',
        userSelect: 'none',
        pointerEvents: 'none'
    };

    const interactiveControlStyles = {
        cursor: 'pointer',
        pointerEvents: 'auto'
    };

    const textStyles = {
        ...nonInteractiveStyles
    };

    const textInputStyles = {
        cursor: 'text',
        pointerEvents: 'auto',
        userSelect: 'text'
    };

    const buttonStyles = {
        ...interactiveControlStyles
    };

    // Initialize form with initialData if it exists (supports both single and multiple KBs)
    useEffect(() => {
        if (initialData) {
            // Handle both old format (knowledge_base) and new format (knowledge_bases)
            const knowledgeBases = initialData.knowledge_bases || (initialData.knowledge_base ? [initialData.knowledge_base] : []);

            if (knowledgeBases.length > 0) {
                // Set form value with array of KB IDs
                form.setFieldsValue({
                    knowledgeBaseIds: knowledgeBases.map(kb => kb.id)
                });

                // Set the selected knowledge bases for displaying details
                setSelectedKnowledgeBases(knowledgeBases);

                // Populate options with the initial data
                setOptions(knowledgeBases.map(kb => ({
                    value: kb.id,
                    knowledgeBase: kb,
                    label: kb.name
                })));
            }
        }
    }, [initialData, form, setSelectedKnowledgeBases, setOptions]);

    const fetchKnowledgeBases = useCallback(async (searchTerm = '', page = 1, isInitialLoad = false) => {
        try {
            setLoading(true);
            const response = await getknowledgeDatabases({
                page,
                size: pageSize,
                search: searchTerm
            });

            if (isInitialLoad && response.data.length === 0) {
                setHasNoKnowledgeBases(true);
                return;
            }
            setHasNoKnowledgeBases(false);

            const formattedOptions = response.data.map(kb => ({
                value: kb.id.toString(),
                knowledgeBase: kb,
                label: (
                    <Flex vertical style={{ padding: '12px' }}>
                        <Flex align="center" style={{ marginBottom: '4px' }}>
                            <DatabaseOutlined style={{ marginInlineEnd: '8px', color: '#1890ff' }} />
                            <Text strong style={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)' }}>{kb.name}</Text>
                        </Flex>
                        <Text type="secondary" style={{ fontSize: '13px', marginBottom: '4px', color: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' }}>
                            {kb.description}
                        </Text>
                        <Row gutter={16}>
                            <Col>
                                <Text type="secondary" style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' }}>
                                    {t('qa')}: {kb.qa_count}
                                </Text>
                            </Col>
                            <Col>
                                <Text type="secondary" style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' }}>
                                    {t('info')}: {kb.business_info_count}
                                </Text>
                            </Col>
                        </Row>
                    </Flex>
                )
            }));

            setOptions(prevOptions => {
                // Filter out any existing 'load-more' option
                const filteredOptions = page === 1 ? [] : prevOptions.filter(opt => opt.value !== 'load-more');

                // Combine previous and new options
                const combinedOptions = [...filteredOptions, ...formattedOptions];

                // Add load-more button at the end if there are more results
                if (response.metadata.has_next) {
                    combinedOptions.push({
                        value: 'load-more',
                        disabled: true,
                        label: (
                            <Flex justify="center" style={{ padding: '8px' }}>
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        fetchKnowledgeBases(form.getFieldValue(['knowledgeBase', 'name']), response.metadata.page + 1);
                                    }}
                                >
                                    {t('load_more')}
                                </Button>
                            </Flex>
                        ),
                    });
                }

                return combinedOptions;
            });
            setCurrentPage(response.metadata.page);
        } catch (error) {
            // Error handled by UI feedback
        } finally {
            setLoading(false);
        }
    }, [setLoading, isDarkMode, t]);

    const debouncedSearch = useMemo(() => debounce((value) => {
        setCurrentPage(1);
        fetchKnowledgeBases(value, 1);
    }, 300), [fetchKnowledgeBases]);

    const handleSearch = (value) => {
        // For multi-select, just trigger search without clearing
        debouncedSearch(value || '');
    };

    /**
     * Handle multi-select change - updates both form and selected state.
     */
    const handleChange = (selectedIds) => {
        // Update form value
        form.setFieldsValue({
            knowledgeBaseIds: selectedIds
        });

        // Update selected KBs from our options
        const allKnowledgeBases = options
            .filter(opt => opt.knowledgeBase)
            .map(opt => opt.knowledgeBase);

        const newSelectedKBs = allKnowledgeBases.filter(kb => selectedIds.includes(kb.id));
        setSelectedKnowledgeBases(newSelectedKBs);

        // Notify parent component about the form update
        if (onUpdate) {
            onUpdate();
        }

        // Inform parent that KBs have been assigned
        if (onAssigned && selectedIds.length > 0) {
            onAssigned(newSelectedKBs);
        }
    };

    /**
     * Custom tag render for KB select - shows database icon with name properly aligned.
     */
    const renderKBTag = useCallback(({ value, closable, onClose }) => {
        const kb = options.find(opt => opt.value === value)?.knowledgeBase;
        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '2px 8px 2px 6px',
                    background: isDarkMode ? '#1f1f1f' : '#f5f5f5',
                    border: `1px solid ${isDarkMode ? '#434343' : '#d9d9d9'}`,
                    borderRadius: 4,
                    marginInlineEnd: 4,
                    marginBlock: 2,
                    lineHeight: '20px',
                }}
            >
                <DatabaseOutlined style={{ color: '#1890ff', fontSize: 14 }} />
                <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {kb?.name || value}
                </span>
                {closable && (
                    <span
                        onClick={onClose}
                        style={{ cursor: 'pointer', marginLeft: 4, color: isDarkMode ? '#8c8c8c' : '#595959' }}
                    >
                        ×
                    </span>
                )}
            </span>
        );
    }, [options, isDarkMode]);

    // Update selectedKnowledgeBases when initialData changes
    useEffect(() => {
        if (initialData?.knowledge_bases) {
            setSelectedKnowledgeBases(initialData.knowledge_bases);
        } else if (initialData?.knowledge_base) {
            setSelectedKnowledgeBases([initialData.knowledge_base]);
        }
    }, [initialData]);

    // Add createdKB to selection when a new knowledge base is created
    useEffect(() => {
        if (createdKB) {
            setSelectedKnowledgeBases(prev => {
                // Avoid duplicates
                if (prev.some(kb => kb.id === createdKB.id)) return prev;
                return [...prev, createdKB];
            });
        }
    }, [createdKB]);

    const handleCreateKnowledgeBase = async (values) => {
        try {
            setCreating(true);
            const response = await addKnowledgeDatabase(values);
            const newKB = response.data;
            setCreatedKB(newKB);

            // Add newly created KB to options so Select can display it
            setOptions(prev => [{
                value: newKB.id,
                knowledgeBase: newKB,
                label: newKB.name
            }, ...prev.filter(opt => opt.value !== newKB.id)]);

            // Add the newly created KB ID to the form field array
            const currentIds = form.getFieldValue('knowledgeBaseIds') || [];
            form.setFieldsValue({
                knowledgeBaseIds: [...currentIds, newKB.id]
            });

            // Validate the field to update form state
            form.validateFields(['knowledgeBaseIds']).then(() => {
                if (onUpdate) {
                    onUpdate();
                }
                if (onAssigned) {
                    onAssigned([...selectedKnowledgeBases, newKB]);
                }
            }).catch(() => { });

            setCurrentStep(1);
            {
                const text = resolveBackendMessage(t, newKB);
                if (text) message.success(text);
            }

            // Notify parent (tour) that creation succeeded
            if (onCreated) {
                onCreated(newKB);
            }

        } catch (error) {
            handleError(t('error_creating_knowledge_base'));
        } finally {
            setCreating(false);
        }
    };

    const handleAddEntry = async (values) => {
        if (!createdKB?.id) {
            message.error(t('knowledge_base_not_created'));
            return;
        }

        try {
            setAddingEntry(true);
            const api = entryTypeTab === 'qa' ? createOrUpdateQAEntry : createOrUpdateBusinessInfoEntry;
            const entryData = entryTypeTab === 'qa'
                ? {
                    knowledge_base_id: createdKB.id,
                    question: values.question,
                    answer: values.answer
                }
                : {
                    knowledge_base_id: createdKB.id,
                    content: values.content
                };

            const response = await api(entryData);
            if (response.success) {
                // Reset the appropriate form based on the active tab
                if (entryTypeTab === 'qa') {
                    qaForm.resetFields();
                } else {
                    businessForm.resetFields();
                }
                {
                    const text = resolveBackendMessage(t, response);
                    if (text) message.success(text);
                }

                // Update entries state with the new entry including its ID
                const newEntry = entryTypeTab === 'qa'
                    ? {
                        id: response.data.id,
                        question: values.question,
                        answer: values.answer
                    }
                    : {
                        id: response.data.id,
                        content: values.content
                    };

                setEntries(prev => {
                    const updatedEntries = {
                        ...prev,
                        [entryTypeTab]: [...prev[entryTypeTab], newEntry]
                    };

                    // Update the form with the current entries
                    const currentKnowledgeBase = form.getFieldValue('knowledgeBase');
                    form.setFieldsValue({
                        knowledgeBase: {
                            ...currentKnowledgeBase,
                            entries: updatedEntries
                        }
                    });

                    return updatedEntries;
                });

                // Update the knowledge base counts in the options
                setOptions(prevOptions => {
                    if (!prevOptions || !Array.isArray(prevOptions)) {
                        return [];
                    }
                    return prevOptions.map(option => {
                        if (!option || !option.knowledgeBase || option.knowledgeBase.id !== createdKB.id) {
                            return option;
                        }
                        const updatedKB = { ...option.knowledgeBase };
                        if (entryTypeTab === 'qa') {
                            updatedKB.qa_count = (updatedKB.qa_count || 0) + 1;
                        } else {
                            updatedKB.business_info_count = (updatedKB.business_info_count || 0) + 1;
                        }
                        return {
                            ...option,
                            knowledgeBase: updatedKB,
                            label: (
                                <Flex vertical style={{ padding: '12px' }}>
                                    <Flex align="center" style={{ marginBottom: '4px' }}>
                                        <DatabaseOutlined style={{ marginInlineEnd: '8px', color: '#1890ff' }} />
                                        <Text strong style={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)' }}>{updatedKB.name}</Text>
                                    </Flex>
                                    <Text type="secondary" style={{ fontSize: '13px', marginBottom: '4px', color: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' }}>
                                        {updatedKB.description}
                                    </Text>
                                    <Row gutter={16}>
                                        <Col>
                                            <Text type="secondary" style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' }}>
                                                {t('qa')}: {updatedKB.qa_count || 0}
                                            </Text>
                                        </Col>
                                        <Col>
                                            <Text type="secondary" style={{ fontSize: '12px', color: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' }}>
                                                {t('info')}: {updatedKB.business_info_count || 0}
                                            </Text>
                                        </Col>
                                    </Row>
                                </Flex>
                            )
                        };
                    });
                });
            } else {
                handleError(response.message || t('error_creating_updating_entry'));
            }
        } catch (error) {
            handleError(t('error_creating_updating_entry'));
        } finally {
            setAddingEntry(false);
        }
    };

    const handleDeleteEntry = async (entryId) => {
        try {
            const payload = await deleteEntry(entryId);
            if (payload?.success) {
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);

                if (entryTypeTab === 'qa') {
                    setEntries(prev => ({
                        ...prev,
                        qa: prev.qa.filter(entry => entry.id !== entryId)
                    }));
                    form.setFieldsValue({
                        knowledgeBase: {
                            ...form.getFieldValue('knowledgeBase'),
                            entries: {
                                ...form.getFieldValue('knowledgeBase').entries,
                                qa: entries.qa.filter(entry => entry.id !== entryId)
                            }
                        }
                    });
                } else {
                    setEntries(prev => ({
                        ...prev,
                        business: prev.business.filter(entry => entry.id !== entryId)
                    }));
                    form.setFieldsValue({
                        knowledgeBase: {
                            ...form.getFieldValue('knowledgeBase'),
                            entries: {
                                ...form.getFieldValue('knowledgeBase').entries,
                                business: entries.business.filter(entry => entry.id !== entryId)
                            }
                        }
                    });
                }
            } else {
                message.error(t('failed_to_delete_entry'));
            }
        } catch (error) {
            message.error(t('error_deleting_entry'));
        }
    };

    /**
     * runBeautify callback for QA entries — used by BeautifyControl.
     * Takes the current {question, answer} compound value and returns
     * the normalized {suggestions, coinsAmount, analysis, warnings} shape.
     */
    const runBeautifyQA = useCallback(async (value) => {
        if (!createdKB?.id) {
            message.error(t('knowledge_base_not_created'));
            return { suggestions: [] };
        }
        try {
            const payload = {
                entry_type: 'question_answer',
                question: value?.question || '',
                answer: value?.answer || '',
            };
            const data = await beautifyEntry(createdKB.id, payload);
            const root = data?.data || data || {};

            const rawWarnings = root?.warnings || [];
            const warnings = Array.isArray(rawWarnings) ? rawWarnings : [];

            let rawSuggestions = root?.suggestions || data?.suggestions || [];
            if (!Array.isArray(rawSuggestions)) rawSuggestions = [];

            // Normalize: keep compound {question, answer} objects as-is for QA
            const suggestions = rawSuggestions.map(s => {
                if (typeof s === 'object' && (s?.question || s?.answer)) return s;
                if (typeof s === 'string') return { question: s, answer: '' };
                if (s?.content && typeof s.content === 'string') return { question: s.content, answer: '' };
                return { question: String(s ?? ''), answer: '' };
            });

            const coinsAmount = typeof root.coins_amount === 'number'
                ? Math.abs(root.coins_amount)
                : typeof data?.coins_amount === 'number' ? Math.abs(data.coins_amount) : null;

            const analysis = root.analysis || data?.analysis || '';

            if (!suggestions.length) {
                message.info(t('no_suggestions_available'));
            }

            return { suggestions, coinsAmount, analysis, warnings };
        } catch (error) {
            message.error(error?.response?.data?.message || t('something_went_wrong'));
            return { suggestions: [] };
        }
    }, [createdKB, t]);

    /**
     * runBeautify callback for Business Info entries.
     */
    const runBeautifyBusiness = useCallback(async (value) => {
        if (!createdKB?.id) {
            message.error(t('knowledge_base_not_created'));
            return { suggestions: [] };
        }
        try {
            const payload = {
                entry_type: 'business_info',
                content: typeof value === 'string' ? value : '',
            };
            const data = await beautifyEntry(createdKB.id, payload);
            const root = data?.data || data || {};

            const rawWarnings = root?.warnings || [];
            const warnings = Array.isArray(rawWarnings) ? rawWarnings : [];

            let rawSuggestions = root?.suggestions || data?.suggestions || [];
            if (!Array.isArray(rawSuggestions)) rawSuggestions = [];

            const suggestions = rawSuggestions.map(s => {
                if (typeof s === 'string') return s;
                if (s?.content && typeof s.content === 'string') return s.content;
                return String(s ?? '');
            }).filter(Boolean);

            const coinsAmount = typeof root.coins_amount === 'number'
                ? Math.abs(root.coins_amount)
                : typeof data?.coins_amount === 'number' ? Math.abs(data.coins_amount) : null;

            const analysis = root.analysis || data?.analysis || '';

            if (!suggestions.length) {
                message.info(t('no_suggestions_available'));
            }

            return { suggestions, coinsAmount, analysis, warnings };
        } catch (error) {
            message.error(error?.response?.data?.message || t('something_went_wrong'));
            return { suggestions: [] };
        }
    }, [createdKB, t]);

    useEffect(() => {
        if (!form.getFieldValue(['knowledgeBase', 'id'])) {
            fetchKnowledgeBases('', 1, true);
        }
    }, [form, fetchKnowledgeBases]);

    if (isCreatingKB) {
        return (
            <Flex vertical style={{
                width: '100%'
            }}>
                {currentStep === 0 && <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => {
                        setIsCreatingKB(false);
                        createForm.resetFields();
                        setCurrentKB(null);
                        setCurrentStep(0);
                        setCreatedKB(null);
                        setEntries({ qa: [], business: [] });
                    }}
                    style={{ padding: 0, marginBottom: 8, width: 'fit-content' }}
                >
                    {t('back_to_selection')}
                </Button>}

                {currentStep === 0 ? (
                    <Form
                        form={createForm}
                        layout="vertical"
                        onFinish={handleCreateKnowledgeBase}
                        style={{ maxWidth: '100%' }}
                        data-tour="kb-create-form"
                    >
                        <Row gutter={[16, 0]} style={{ margin: 0 }}>
                            <Col xs={24} md={24}>
                                <Form.Item
                                    name="name"
                                    label={t('name')}
                                    rules={[{ required: true, message: t('please_enter_knowledge_base_name') }]}
                                    style={{ marginBottom: 24 }}
                                >
                                    <Input
                                        placeholder={t('enter_knowledge_base_name')}
                                        size="medium"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={24}>
                                <Form.Item
                                    name="description"
                                    label={t('description')}
                                    style={{ marginBottom: 16 }}
                                >
                                    <Input.TextArea
                                        placeholder={t('enter_knowledge_base_description')}
                                        rows={4}
                                        size="medium"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={24}>
                                <Form.Item style={{ marginBottom: 0 }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={creating}
                                        block
                                        size="medium"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            createForm.submit();
                                        }}
                                    >
                                        {t('next')}
                                    </Button>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                ) : (
                    <Flex vertical gap={16}>
                        <Flex
                            align="center"
                            gap="middle"
                            style={{
                                ...nonInteractiveStyles,
                                padding: '12px 16px',
                                borderRadius: 8,
                                background: isDarkMode
                                    ? 'linear-gradient(135deg, rgba(24,144,255,0.10) 0%, rgba(24,144,255,0.04) 100%)'
                                    : 'linear-gradient(135deg, rgba(24,144,255,0.08) 0%, rgba(24,144,255,0.02) 100%)',
                                border: `1px solid ${isDarkMode ? 'rgba(24,144,255,0.18)' : 'rgba(24,144,255,0.15)'}`,
                            }}
                        >
                            <DatabaseOutlined style={{ fontSize: 22, color: '#1890ff', ...nonInteractiveStyles }} />
                            <Text strong style={{ fontSize: 15, ...nonInteractiveStyles }}>{createdKB?.name}</Text>
                        </Flex>

                        <Row gutter={[24, 16]} style={{ margin: 0 }}>
                            <Col xs={24} md={12}>
                                <Flex vertical gap={16}>
                                    <Form.Item
                                        label={t('entry_type', { defaultValue: 'Entry Type' })}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Select
                                            value={entryTypeTab}
                                            onChange={(value) => {
                                                setEntryTypeTab(value);
                                                if (value === 'qa') {
                                                    qaForm.resetFields();
                                                } else {
                                                    businessForm.resetFields();
                                                }
                                            }}
                                            style={{ width: '100%' }}
                                            options={[
                                                { value: 'qa', label: t('qa_entries') },
                                                { value: 'business', label: t('business_information') },
                                            ]}
                                        />
                                    </Form.Item>

                                    {/* Key forces full unmount/remount to prevent stale shouldUpdate state from React reconciliation */}
                                    {entryTypeTab === 'qa' ? (
                                        <Form
                                            key="qa-entry-form"
                                            form={qaForm}
                                            layout="vertical"
                                            onFinish={handleAddEntry}
                                            style={{ maxWidth: '100%' }}
                                        >
                                            <Form.Item
                                                name="question"
                                                label={t('question')}
                                                rules={[{ required: true, message: t('please_enter_question') }]}
                                                style={{ marginBottom: 16 }}
                                            >
                                                <Input.TextArea
                                                    rows={2}
                                                    placeholder={t('enter_question_here')}
                                                    size="medium"
                                                    style={{ ...textInputStyles }}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                name="answer"
                                                label={t('answer')}
                                                rules={[{ required: true, message: t('please_enter_answer') }]}
                                                style={{ marginBottom: 8 }}
                                            >
                                                <Input.TextArea
                                                    rows={4}
                                                    placeholder={t('enter_answer_here')}
                                                    size="medium"
                                                    style={{ ...textInputStyles }}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                noStyle
                                                shouldUpdate={(prev, current) =>
                                                    prev.question !== current.question ||
                                                    prev.answer !== current.answer
                                                }
                                            >
                                                {() => {
                                                    const question = qaForm.getFieldValue('question') || '';
                                                    const answer = qaForm.getFieldValue('answer') || '';
                                                    const disabled = !question.trim() || !answer.trim();
                                                    return (
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                                                            <BeautifyControl
                                                                value={{ question, answer }}
                                                                onChange={(next) => {
                                                                    if (typeof next === 'object' && (next?.question || next?.answer)) {
                                                                        qaForm.setFieldsValue({
                                                                            question: next.question || '',
                                                                            answer: next.answer || '',
                                                                        });
                                                                    } else if (typeof next === 'string') {
                                                                        qaForm.setFieldsValue({ question: next });
                                                                    }
                                                                }}
                                                                runBeautify={runBeautifyQA}
                                                                beautifyLabel={t('beautify')}
                                                                beautifyIcon={<HighlightOutlined style={{ fontSize: ICON_SIZE }} />}
                                                                revertLabel={t('revert')}
                                                                disabled={disabled}
                                                                buttonStyle={{ color: '#7F56D9' }}
                                                            />
                                                        </div>
                                                    );
                                                }}
                                            </Form.Item>

                                            <Form.Item style={{ marginBottom: 0 }}>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={addingEntry}
                                                    block
                                                    size="medium"
                                                    style={{
                                                        ...interactiveControlStyles,
                                                        borderRadius: 6,
                                                        height: 38,
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        qaForm.validateFields().then(values => {
                                                            handleAddEntry(values);
                                                        });
                                                    }}
                                                >
                                                    {t('add_qa_entry')}
                                                </Button>
                                            </Form.Item>
                                        </Form>
                                    ) : (
                                        <Form
                                            key="business-entry-form"
                                            form={businessForm}
                                            layout="vertical"
                                            onFinish={handleAddEntry}
                                            style={{ maxWidth: '100%' }}
                                        >
                                            <Form.Item
                                                name="content"
                                                label={t('business_information')}
                                                rules={[{ required: true, message: t('please_enter_business_information') }]}
                                                style={{ marginBottom: 8 }}
                                            >
                                                <Input.TextArea
                                                    rows={6}
                                                    placeholder={t('enter_business_information_here')}
                                                    size="medium"
                                                    style={{ ...textInputStyles }}
                                                />
                                            </Form.Item>

                                            <Form.Item
                                                noStyle
                                                shouldUpdate={(prev, current) =>
                                                    prev.content !== current.content
                                                }
                                            >
                                                {() => {
                                                    const content = businessForm.getFieldValue('content') || '';
                                                    const disabled = !content.trim();
                                                    return (
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                                                            <BeautifyControl
                                                                value={content}
                                                                onChange={(next) => {
                                                                    businessForm.setFieldsValue({ content: next });
                                                                }}
                                                                runBeautify={runBeautifyBusiness}
                                                                beautifyLabel={t('beautify')}
                                                                beautifyIcon={<HighlightOutlined style={{ fontSize: ICON_SIZE }} />}
                                                                revertLabel={t('revert')}
                                                                disabled={disabled}
                                                                buttonStyle={{ color: '#7F56D9' }}
                                                            />
                                                        </div>
                                                    );
                                                }}
                                            </Form.Item>

                                            <Form.Item style={{ marginBottom: 0 }}>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={addingEntry}
                                                    block
                                                    size="medium"
                                                    style={{
                                                        ...interactiveControlStyles,
                                                        borderRadius: 6,
                                                        height: 38,
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        businessForm.validateFields().then(values => {
                                                            handleAddEntry(values);
                                                        });
                                                    }}
                                                >
                                                    {t('add_business_information')}
                                                </Button>
                                            </Form.Item>
                                        </Form>
                                    )}
                                </Flex>
                            </Col>

                            <Col xs={24} md={12}>
                                <Flex
                                    vertical
                                    style={{
                                        borderRadius: 8,
                                        border: `1px solid ${isDarkMode ? '#303030' : '#f0f0f0'}`,
                                        background: isDarkMode ? '#1a1a1a' : '#fafafa',
                                        padding: 16,
                                        minHeight: 200,
                                    }}
                                >
                                    {(entries.qa.length > 0 || entries.business.length > 0) ? (
                                        <Flex vertical gap={12}>
                                            {entries.qa.map((entry) => (
                                                <div key={`qa-${entry.id}`}>
                                                    <Tag
                                                        color="blue"
                                                        icon={<QuestionCircleOutlined />}
                                                        style={{ marginBottom: 6, borderRadius: 4, fontSize: 12 }}
                                                    >
                                                        {t('qa')}
                                                    </Tag>
                                                    <QAEntryItem entry={entry} onDelete={handleDeleteEntry} />
                                                </div>
                                            ))}
                                            {entries.business.map((entry) => (
                                                <div key={`biz-${entry.id}`}>
                                                    <Tag
                                                        color="green"
                                                        icon={<InfoCircleOutlined />}
                                                        style={{ marginBottom: 6, borderRadius: 4, fontSize: 12 }}
                                                    >
                                                        {t('business_information')}
                                                    </Tag>
                                                    <BusinessInfoEntryItem entry={entry} onDelete={handleDeleteEntry} />
                                                </div>
                                            ))}
                                        </Flex>
                                    ) : (
                                        <Flex
                                            align="center"
                                            justify="center"
                                            style={{ flex: 1, minHeight: 168 }}
                                        >
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description={
                                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                                        {t('no_entries_yet', { defaultValue: 'No entries yet' })}
                                                    </Text>
                                                }
                                            />
                                        </Flex>
                                    )}
                                </Flex>
                            </Col>
                        </Row>
                    </Flex>
                )}
            </Flex>
        );
    }

    if (hasNoKnowledgeBases && !form.getFieldValue(['knowledgeBase', 'id'])) {
        return (
            <Col xs={24} md={24}>
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ margin: '0', width: '100%', paddingBottom: '3px', ...nonInteractiveStyles }}
                    description={
                        <Space direction='vertical' style={{ width: '100%', ...nonInteractiveStyles }}>
                            <Text type="secondary" style={{ width: '100%', ...nonInteractiveStyles }} >{t('no_knowledge_bases_found')}</Text>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setIsCreatingKB(true);
                                    if (onStartCreate) onStartCreate();
                                }}
                                style={{ width: '100%', ...buttonStyles }}
                                data-tour="kb-create"
                            >
                                {t('create_first_knowledge_base')}
                            </Button>
                        </Space>
                    }
                />
            </Col>
        );
    }

    return (
        <Col xs={24} md={24}>
            <Form.Item
                label={t('knowledge_bases')}
                style={{ marginBottom: 4 }}
                tooltip={t('select_knowledge_bases_tooltip', { defaultValue: 'Select the knowledge bases this agent will use to answer questions' })}
            >
                <Form.Item
                    name="knowledgeBaseIds"
                    rules={[{ required: true, message: t('please_select_knowledge_base', { defaultValue: 'Please select at least one knowledge base' }), type: 'array' }]}
                    style={{ marginBottom: 0 }}
                >
                    <Select
                        mode="multiple"
                        placeholder={t('search_knowledge_base')}
                        loading={loading}
                        style={{ width: '100%' }}
                        options={options.filter(opt => opt.value !== 'load-more').map(opt => ({
                            value: opt.value,
                            label: (
                                <Flex align="center" gap={8}>
                                    <DatabaseOutlined style={{ color: '#1890ff' }} />
                                    <Flex vertical gap={0}>
                                        <Text>{opt.knowledgeBase?.name || opt.label}</Text>
                                        {opt.knowledgeBase?.description && (
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {opt.knowledgeBase.description.substring(0, 50)}
                                                {opt.knowledgeBase.description.length > 50 ? '...' : ''}
                                            </Text>
                                        )}
                                    </Flex>
                                </Flex>
                            )
                        }))}
                        showSearch
                        filterOption={false}
                        onSearch={handleSearch}
                        onChange={handleChange}
                        tagRender={renderKBTag}
                        optionFilterProp="children"
                        data-tour="kb-select"
                        notFoundContent={loading ? <Spin size="small" /> : t('no_matching_knowledge_base_found')}
                        dropdownRender={(menu) => (
                            <Flex vertical>
                                {menu}
                                {loading && (
                                    <Flex justify="center" style={{ padding: '12px' }}>
                                        <Spin size="small" />
                                    </Flex>
                                )}
                                <Divider style={{ margin: '8px 0' }} />
                                <Flex style={{ padding: '8px 12px' }}>
                                    <Button
                                        type="link"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            setIsCreatingKB(true);
                                            if (onStartCreate) onStartCreate();
                                        }}
                                        className="create-kb-button"
                                        data-tour="kb-create"
                                    >
                                        <span className="create-kb-button-text">{t('create_new_knowledge_base')}</span>
                                    </Button>
                                </Flex>
                            </Flex>
                        )}
                    />
                </Form.Item>
            </Form.Item>
        </Col>
    );
};

export default KnowledgeBaseSection;
